import { toast } from "@suraasa/placebo-ui"
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import {
  PreferredPromptData,
  ToolType,
  UnitPlanForm,
  UnitPlanGenerateResponse,
  UnitPlanPromptData,
} from "api/resources/aiTools/types"
import { APIResponse } from "api/types"
import { APIError } from "api/utils"
import { getToolName } from "features/AItools/helper"
import {
  StreamFetchProps,
  StreamQuery,
  StreamResponseEnum,
  useStreamQuery,
} from "features/AItools/hooks/useStreamQuery"
import { useEffect, useState } from "react"
import { flushSync } from "react-dom"
import { UseFormSetError } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { mapErrors } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

// type OutputModeType =
//   | "unitPlanTopic"
//   | "unitPlanIntermediate"
//   | "unitPlanDivision"
//   | ""

export type UnitPlannerReturnType = {
  currentResponseId: number | null
  setCurrentResponseId: (id: number | null) => void
  promptDataId: string | number | null
  setPromptDataId: React.Dispatch<React.SetStateAction<string | number | null>>
  responseIds: number[] | null
  setResponseIds: React.Dispatch<React.SetStateAction<number[] | null>>
  overviewData: UnitPlanPromptData | undefined

  userVote: UseMutationResult<
    never,
    unknown,
    {
      reaction: boolean | undefined | null
    },
    unknown
  >
  output: string | undefined
  isLoading: boolean
  stream: Omit<StreamQuery, "refetchAsync"> & {
    refetchAsync: <T>(
      props: Omit<StreamFetchProps<T>, "toolType">
    ) => Promise<void>
  }
  //   outputMode: OutputModeType

  regenerateStream: StreamQuery
  generateUnitPlanPrompt: UseMutationResult<
    APIResponse<UnitPlanGenerateResponse>,
    any,
    {
      data: UnitPlanForm
      setError: UseFormSetError<UnitPlanForm>
    },
    unknown
  >
  regenerateResponse: UseMutationResult<
    APIResponse<{
      id: number
    }>,
    unknown,
    {
      reason: string
    },
    unknown
  >

  isPositiveResponse: boolean | null | undefined
  pageLoading: boolean
  resetState: () => void
  onBack: () => void
  handleCancelApi: () => void
  formData: UnitPlanForm | null
  preferredPromptData: PreferredPromptData | null
}

// export const getOutputMode = ({
//   unitPlanResponses,
//   unitDivisionResponses,
// }: {
//   unitPlanResponses?: { id: number; responseIds: number[] }[] | undefined
//   unitDivisionResponses?: number[] | undefined
// }): OutputModeType => {
//   if (!unitPlanResponses || !unitDivisionResponses) {
//     throw new Error(
//       "unitPlanResponses and unitDivisionResponses should not be undefined"
//     )
//   }

//   if (unitDivisionResponses.length === 0 && unitPlanResponses.length > 0) {
//     return "unitPlanTopic"
//   }
//   if (unitDivisionResponses.length > 0 && unitPlanResponses.length === 0) {
//     return "unitPlanIntermediate"
//   }
//   if (unitDivisionResponses.length > 0 && unitPlanResponses.length > 0) {
//     return "unitPlanDivision"
//   }

//   return ""
// }

export const useUnitPlanner = (): UnitPlannerReturnType => {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()

  const [overviewData, setOverviewData] = useState<
    UnitPlanPromptData | undefined
  >()
  const [isLoading, setIsLoading] = useState(false)
  const [isPositiveResponse, setIsPositiveResponse] = useState<
    boolean | null | undefined
  >()
  const [currentResponseId, setCurrentResponseId] = useState<number | null>(
    null
  )
  const [promptDataId, setPromptDataId] = useState<string | number | null>(id)
  const [responseIds, setResponseIds] = useState<number[] | null>(null)
  //   const [outputMode, setOutputMode] = useState<OutputModeType>("")

  const [pageLoading, setPageLoading] = useState(false)
  const [output, setOutput] = useState<string>("")
  const [formData, setFormData] = useState<UnitPlanForm | null>(null)

  const getContent = async (id: number) => {
    // resetStream()
    setOutput("")
    setIsLoading(true)
    const res = await api.aiTools.unitPlanner.retrieveContent({
      urlParams: {
        id: id,
      },
    })
    if (res instanceof APIError) {
      toast.error(res.message || "Something went wrong")
      setIsLoading(false)
      return
    }

    setIsPositiveResponse(res.isPositiveResponse)
    setOutput(res.output)
    setIsLoading(false)
    // resetStream()
  }

  const resetState = () => {
    setOutput("")
    setPromptDataId(null)
    setCurrentResponseId(null)
    setIsPositiveResponse(undefined)
    setOverviewData(undefined)
    setResponseIds(null)
  }
  const handleCancelApi = () => {
    if (streamTitle.isLoading) streamTitle.handleCancel()
    if (streamOutput.isLoading) streamOutput.handleCancel()
    if (regenerateStream.isLoading) regenerateStream.handleCancel()
  }

  const onBack = () => {
    navigate(routes.aiTools.unitPlanner, { replace: true })
    resetState()
    handleCancelApi()
  }
  // This flow is for when user clicks on history item
  // If that is the case then we just fetch the overview and the latest generated output
  useEffect(() => {
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPageLoading(true)
      api.aiTools.unitPlanner
        .getPromptData({
          urlParams: {
            id: id!,
          },
        })
        .then(async data => {
          const { unitPlanResponses } = data

          setPromptDataId(id)
          setOverviewData(data)

          if (unitPlanResponses) {
            setCurrentResponseId(unitPlanResponses[0].responseIds[0])
            setResponseIds(unitPlanResponses[0].responseIds)
            await getContent(unitPlanResponses[0].responseIds[0])
          }
          //   const { unitPlanResponses, unitDivisionResponses } = data
          //   const mode = getOutputMode({
          //     unitPlanResponses,
          //     unitDivisionResponses,
          //   })
          //   setOutputMode(mode)
          //   setOverviewData(data)
          //   if (mode === "unitPlanIntermediate") {
          //     setCurrentResponseId(unitDivisionResponses[0])
          //     setResponseIds(unitDivisionResponses)
          //     return
          //   }

          //   if (mode === "unitPlanTopic" && unitPlanResponses) {
          //     setCurrentResponseId(unitPlanResponses[0]?.responseIds[0])
          //     setResponseIds(unitPlanResponses[0]?.responseIds)
          //     return
          //   }
          setPageLoading(false)
        })
        .catch(error => {
          console.error("Error fetching prompt data:", error)
          setPageLoading(false)
        })
    }
  }, [id])

  const overviewAPI = async (id: string | number | null) => {
    const res = await api.aiTools.unitPlanner.getPromptData({
      urlParams: {
        id: id!,
      },
    })
    if (res instanceof APIError) {
      toast.error("Prompt failed to generate")
      return
    }

    const { unitPlanResponses } = res
    // const mode = getOutputMode({
    //   unitPlanResponses,
    //   unitDivisionResponses,
    // })
    // setOutputMode(mode)
    setOverviewData(res)
    setPromptDataId(id)
    if (unitPlanResponses) {
      setCurrentResponseId(unitPlanResponses[0]?.responseIds[0])
      setResponseIds(unitPlanResponses[0]?.responseIds)
    }
  }

  const generateUnitPlanPrompt = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: UnitPlanForm
      setError: UseFormSetError<UnitPlanForm>
    }) => {
      setFormData(data)
      return api.aiTools.generateUnitPlanPrompt({
        data: {
          subject: data.subject,
          ...(data.book ? { book: data.book } : {}),
          ...(data.topic ? { topic: data.topic } : {}),
          grade: data.grade,
          country: data.country,
          curriculum: data.curriculum,
          classDuration: `00:${data.classDuration}:00`,
          unitPlanType: data.unitPlanType,
          ...(data.semesterDuration
            ? { semesterDuration: data.semesterDuration }
            : {}),
          ...(data.numberOfDays ? { numberOfDays: data.numberOfDays } : {}),
        },
      })
    },
    onSuccess: async (res, { setError }) => {
      flushSync(() => {
        resetState()
        setOverviewData({
          id: res.id,
          grade: res.grade,
          classDuration: res.classDuration,
          subject: res.subject,
          curriculum: res.curriculum,
          unitPlanType: res.unitPlanType,
          country: res.country,
          topic: res.topic,
          numberOfDays: res.numberOfDays,
          title: `New ${getToolName(ToolType.unitPlanner)}`,
        })
      })

      await generateUnitPlan.mutateAsync({ id: res.id, setError })
    },
    onError: (err: any, { setError }) => {
      if (err instanceof APIError) {
        mapErrors(setError, err, [
          ["subject"],
          ["book"],
          ["topic"],
          ["grade"],
          ["country"],
          ["curriculum"],
          ["classDuration"],
          ["unitPlanType"],
          ["semesterDuration"],
          ["numberOfDays"],
        ])
      }
    },
  })

  const generateUnitPlan = useMutation({
    mutationFn: ({
      id,
    }: {
      id: number
      setError: UseFormSetError<UnitPlanForm>
    }) =>
      api.aiTools.generateUnitPlan({
        data: {
          promptDataId: id,
          unitPlanType: "topic",
        },
      }),
    onSuccess: async res => {
      const { responseId, id } = res
      resetState()
      flushSync(() => {
        setCurrentResponseId(responseId)
        setResponseIds([responseId])
      })
      await streamOutput.refetchAsync({
        currentResponseId: responseId,
      })
      await streamTitle.refetchAsync({
        currentResponseId: responseId,
      })
      try {
        await overviewAPI(id)
      } catch (error) {
        console.error("Error fetching overview data:", error)
      }
      queryClient.refetchQueries({
        queryKey: queries.aiTools.listHistory(ToolType.unitPlanner).queryKey,
        type: "active",
        exact: true,
      })
    },
    onError: (err: any, { setError }) => {
      if (err instanceof APIError) {
        mapErrors(setError, err, [
          ["subject"],
          ["book"],
          ["topic"],
          ["grade"],
          ["country"],
          ["curriculum"],
          ["classDuration"],
          ["unitPlanType"],
          ["semesterDuration"],
          ["numberOfDays"],
        ])
      }
    },
  })

  const streamTitle = useStreamQuery({
    responseType: StreamResponseEnum.title,
    toolType: ToolType.unitPlanner,
  })

  const streamOutput = useStreamQuery({
    responseType: StreamResponseEnum.generate,
    toolType: ToolType.unitPlanner,
    onError: () => {
      toast.error("Generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const regenerateStream = useStreamQuery({
    responseType: StreamResponseEnum.regenerate,
    toolType: ToolType.unitPlanner,
    onSuccess: () => {
      toast.success("Re-generated Successfully")
    },
    onError: () => {
      toast.error("Re-generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const userVote = useMutation({
    mutationFn: async ({
      reaction,
    }: {
      reaction: boolean | undefined | null
    }) => {
      const eventName = reaction
        ? TrackingEvents.aiToolOutputUpvoted
        : TrackingEvents.aiToolOutputDownvoted
      const toolType = ToolType.unitPlanner

      trackingService.trackEvent(eventName, {
        tool_type: toolType,
      })

      return api.aiTools.unitPlanner.updateData({
        urlParams: { id: currentResponseId!, type: toolType },
        data: {
          isPositiveResponse: reaction,
        },
      })
    },
  })

  const regenerateResponse = useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      api.aiTools.unitPlanner.regenerateResponse({
        urlParams: {
          id: currentResponseId!,
        },
        data: {
          regenerateInstruction: reason,
        },
      }),
    onSuccess: async (res, { reason }) => {
      flushSync(() => {
        setCurrentResponseId(res.id)
        setOutput("")
      })

      await regenerateStream.refetchAsync({
        currentResponseId: res.id,
        regenReason: reason,
      })
      try {
        await overviewAPI(id || promptDataId)
      } catch (error) {
        console.error("Error fetching overview data:", error)
      }
    },
    onError: err => {
      if (err instanceof APIError) {
        if ((err?.statusCode || 0) >= 500) {
          toast.error("We're unable to process your request", {
            description: "Please try again later",
          })
          return
        }
        if (err.errors.message) {
          toast.error(err.errors.message)
          return
        }
      } else {
        toast.error("We're unable to process your request", {
          description: "Please try again later",
        })
      }
    },
  })

  const preferredPromptData = useQuery({
    queryFn: () => api.aiTools.retrievePreferredPromptData(),
    queryKey: ["preferredPromptData"],
  })

  return {
    promptDataId,

    setPromptDataId,
    currentResponseId,
    setCurrentResponseId: id => {
      setCurrentResponseId(id)
      if (id) {
        getContent(id)
      }
    },
    responseIds,
    setResponseIds,
    stream: {
      ...streamOutput,
      refetchAsync: ({ currentResponseId: id }) =>
        streamOutput.refetchAsync({
          currentResponseId: id,
        }),
    },
    generateUnitPlanPrompt,
    regenerateResponse,
    overviewData,
    userVote,
    regenerateStream,

    output,
    isLoading:
      isLoading || regenerateStream.isLoading || streamOutput.isLoading,
    isPositiveResponse,
    resetState,
    onBack,
    pageLoading,
    handleCancelApi,
    formData,
    preferredPromptData: preferredPromptData.data ?? null,
  }
}
